import React from 'react';
import { motion } from 'framer-motion';
import { FileTextIcon, CircleCheckIcon, ClockIcon, AlertTriangleIcon, UsersIcon, MessageCircleIcon, ArrowUpIcon, ArrowDownIcon, BarChart3Icon, TargetIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { analyticsCopy, analyticsKpis, analyticsBenefits, dashboardMeta, type Bi, type KpiKey } from '../../data/permisAnalytics';
import { VolumePanel, TimesPanel, FunnelPanel, SectorsPanel, AtnaInsightPanel, DashboardSidebar, DateRange } from './analytics/AnalyticsPanels';
const EASE = [0.23, 1, 0.32, 1] as const;
const TOWN_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/4f179b04-b5f7-4ca1-b026-76d123694bce.jpg';
const KPI_ICONS: Record<KpiKey, typeof FileTextIcon> = {
  received: FileTextIcon,
  complete: CircleCheckIcon,
  delay: ClockIcon,
  risk: AlertTriangleIcon,
  inspections: UsersIcon,
  status: MessageCircleIcon
};
const BENEFIT_ICONS = {
  bars: BarChart3Icon,
  clock: ClockIcon,
  people: UsersIcon,
  target: TargetIcon
} as const;
export function PermisAnalytics() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const reveal = {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    },
    transition: {
      duration: 0.3,
      ease: EASE
    }
  };
  return <div className="relative max-w-[1640px] mx-auto" data-id="element-4787" data-mp="3vjcglnann8bu">
      {/* Faded town backdrop, top right */}
      <img src={TOWN_IMAGE} alt="" aria-hidden="true" className="hidden xl:block pointer-events-none absolute -top-24 right-[-24px] w-[62%] h-[300px] object-cover object-[50%_60%] opacity-70" style={{
      WebkitMaskImage: 'linear-gradient(to bottom, #000 30%, transparent 100%), linear-gradient(to right, transparent 0%, #000 35%)',
      WebkitMaskComposite: 'source-in',
      maskImage: 'linear-gradient(to bottom, #000 30%, transparent 100%), linear-gradient(to right, transparent 0%, #000 35%)',
      maskComposite: 'intersect'
    }} data-id="element-4788" data-mp="3qk981svgujz4" />

      <div className="relative grid grid-cols-1 xl:grid-cols-[35fr_65fr] gap-10 xl:gap-8 items-start" data-id="element-4789" data-mp="5bqmjr617agi">
        {/* Left — editorial + KPIs */}
        <motion.div {...reveal} className="min-w-0" data-id="element-4790" data-mp="f2v32vbcv3dg">
          <p className="font-sans text-[13px] font-semibold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-4791" data-mp="f1jri9bo4rbs">{pick(analyticsCopy.eyebrow)}</p>
          <h2 className="font-serif text-[2.5rem] sm:text-[3.2rem] 2xl:text-[3.9rem] leading-[1.03] tracking-tight mt-5" data-id="element-4792" data-mp="3ekcmqqe88ktm">
            <span className="block text-[#121A3A]" data-id="element-4793" data-mp="3niy2yew87nr8">{pick(analyticsCopy.line1)}</span>
            <span className="block text-[#121A3A]" data-id="element-4794" data-mp="3shrgg7bijere">{pick(analyticsCopy.line2)}</span>
            <span className="block text-[#4F46E5]" data-id="element-4795" data-mp="3sfenz084pm03">{pick(analyticsCopy.line3)}</span>
          </h2>
          <p className="font-sans text-[17px] md:text-[18px] text-[#5B6478] leading-relaxed mt-6 max-w-[560px]" data-id="element-4796" data-mp="12uth3n26vcua">
            <strong className="font-semibold text-[#121A3A]" data-id="element-4797" data-mp="2s9kxczcv2rl7">{pick(analyticsCopy.bodyLead)}</strong>
            {pick(analyticsCopy.bodyRest)}
          </p>

          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-4798" data-mp="2nazwqytp65w8">
            {analyticsKpis.map(k => {
            const Icon = KPI_ICONS[k.key];
            const bad = k.tone === 'bad-up';
            const Arrow = k.tone === 'good-down' ? ArrowDownIcon : ArrowUpIcon;
            return <li key={k.key} className="rounded-2xl border border-[#EEEEF4] bg-white shadow-[0_12px_30px_-24px_rgba(40,36,120,0.4)] p-4 flex items-start gap-4" data-id="element-4799" data-mp="29keencc2hzze">
                  <span className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${bad ? 'bg-[#FDEEF1]' : 'bg-[#F1F0FD]'}`} aria-hidden="true" data-id="element-4800" data-mp="1zlykt1lnocm7">
                    <Icon className={`w-6 h-6 ${bad ? 'text-[#E2476B]' : 'text-[#4F46E5]'}`} strokeWidth={1.7} data-id="element-4801" data-mp="3tyeopvqzbenw" />
                  </span>
                  <span className="min-w-0" data-id="element-4802" data-mp="v5ftr02gcdz">
                    <span className="block font-sans text-[14px] font-semibold text-[#121A3A] leading-snug" data-id="element-4803" data-mp="wgjlqtihk53">{pick(k.title)}</span>
                    <span className="block font-sans text-[26px] font-semibold text-[#4F46E5] leading-tight mt-1" data-id="element-4804" data-mp="5s5ma30tvh5m">{pick(k.value)}</span>
                    <span className={`flex items-center gap-1 font-sans text-[13px] font-semibold ${bad ? 'text-[#E2476B]' : 'text-[#1E8A45]'}`} data-id="element-4805" data-mp="3s16m7m936hta">
                      <Arrow className="w-3.5 h-3.5" strokeWidth={2.4} aria-hidden="true" data-id="element-4806" data-mp="3e8gynddwt4u2" />
                      {pick(k.trend)}
                    </span>
                    <span className="block font-sans text-[12.5px] text-[#8A90A2]" data-id="element-4807" data-mp="34cdvc47bn05j">{pick(analyticsCopy.vsPeriod)}</span>
                  </span>
                </li>;
          })}
          </ul>
        </motion.div>

        {/* Right — dashboard */}
        <motion.div {...reveal} transition={{
        duration: 0.3,
        delay: 0.05,
        ease: EASE
      }} className="min-w-0 xl:mt-8" data-id="element-4808" data-mp="2vdaaovsiacox">
          <div className="rounded-[22px] border border-[#E9E9F2] bg-white shadow-[0_40px_80px_-50px_rgba(30,30,90,0.5)] overflow-hidden" data-id="element-4809" data-mp="2goh1twhl68av">
            <div className="hidden sm:flex items-center gap-1.5 px-4 py-2.5 border-b border-[#F2F2F7] bg-[#FBFBFD]" aria-hidden="true" data-id="element-4810" data-mp="2bqfgf4wotww8">
              <span className="w-2.5 h-2.5 rounded-full bg-[#DADCE5]" data-id="element-4811" data-mp="2pn2e59wgsr37" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#DADCE5]" data-id="element-4812" data-mp="2kpapcor49l38" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#DADCE5]" data-id="element-4813" data-mp="2zmtdppzvtwa5" />
            </div>
            <div className="flex" data-id="element-4814" data-mp="2uoi3vf9l7lrx">
              <DashboardSidebar isFR={isFR} data-id="element-4815" data-mp="2ungq3316sl6i" />
              <div className="flex-1 min-w-0 p-3 sm:p-5 bg-[#FCFCFE]" data-id="element-4816" data-mp="38fn4ajks4i3b">
                <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-4817" data-mp="38ev754bjacax">
                  <p className="font-sans text-[18px] sm:text-[20px] font-semibold text-[#121A3A]" data-id="element-4818" data-mp="34el3vzf4lzzr">{pick(dashboardMeta.title)}</p>
                  <DateRange isFR={isFR} data-id="element-4819" data-mp="3ibhr7qw1czbw" />
                </div>
                <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1.45fr_1fr] gap-3" data-id="element-4820" data-mp="3dg2dr01xx5g0">
                  <VolumePanel isFR={isFR} data-id="element-4821" data-mp="2b2cy0tk0vq3d" />
                  <TimesPanel isFR={isFR} data-id="element-4822" data-mp="2azicszt6q18w" />
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-[1fr_1.1fr_1.1fr] gap-3" data-id="element-4823" data-mp="2f3q2p0bdpn8p">
                  <FunnelPanel isFR={isFR} data-id="element-4824" data-mp="2k0oxzm6s408h" />
                  <SectorsPanel isFR={isFR} data-id="element-4825" data-mp="2osggrajp11o2" />
                  <div className="md:col-span-2 2xl:col-span-1" data-id="element-4826" data-mp="2oub1anr1yrrm">
                    <AtnaInsightPanel isFR={isFR} data-id="element-4827" data-mp="2tsmaqxdfr09b" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Benefits */}
      <ul className="mt-12 pt-8 border-t border-[#EEEEF4] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0 lg:divide-x divide-[#EEEEF4]" data-id="element-4828" data-mp="32s9cjka2u82w">
        {analyticsBenefits.map(b => {
        const Icon = BENEFIT_ICONS[b.key];
        return <li key={b.key} className="flex items-start gap-4 lg:px-6 first:lg:pl-0" data-id="element-4829" data-mp="18guln05by2lk">
              <span className="w-16 h-16 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-4830" data-mp="1dfnozzqwkd80">
                <Icon className="w-7 h-7 text-[#4F46E5]" strokeWidth={1.6} data-id="element-4831" data-mp="113lnqj8110oa" />
              </span>
              <span className="min-w-0" data-id="element-4832" data-mp="w3zai9s77kfh">
                <span className="block font-sans text-[16px] font-semibold text-[#121A3A]" data-id="element-4833" data-mp="w50nijsrygzu">{pick(b.title)}</span>
                <span className="block font-sans text-[14.5px] text-[#5B6478] leading-snug mt-1" data-id="element-4834" data-mp="r6qmvtoa6o6u">{pick(b.body)}</span>
              </span>
            </li>;
      })}
      </ul>
    </div>;
}